import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  IconButton,
  MenuItem,
  Select,
  Paper,
  Container,
  DialogActions,
  InputLabel,
  FormControl,
} from '@mui/material';
import { Remove } from '@mui/icons-material';

interface Skill {
  id?: number;
  name: string;
}

interface Language {
  id?: number;
  name: string;
  proficiency: string;
}

interface SkillFormProps {
  skills: { [categoryName: string]: { skill: Skill[] } }; // This is the structure from your backend
  languages: Language[];
  onUpdateSkill: (updatedSkills: { id?: number; name: string; categoryId: number; }[]) => void;
  onCreateSkill: (createSkill: { name: string; categoryId: number; userId: number; }) => void;
  onRemoveSkill: (skillId?: number) => void;
  onCreateLanguage: (language: { name: string; proficiency: string; userId: number }) => void;
  onRemoveLanguage: (languageId?: number) => void;
  handleClose: () => void;
}

const SkillForm: React.FC<SkillFormProps> = ({ 
  skills,
  languages,
  onUpdateSkill,
  onCreateLanguage,
  onRemoveLanguage,
  onCreateSkill, 
  onRemoveSkill, 
  handleClose 
}) => {
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [newCategory, setNewCategory] = useState<string>('');
  const [skillInput, setSkillInput] = useState<string>('');

  const [languageInput, setLanguageInput] = useState<string>('');
  const [proficiencyInput, setProficiencyInput] = useState<string>('');
  const [languagesState, setLanguagesState] = useState<Language[]>(languages);

  // Convert backend data to a manageable format
  const [categories, setCategories] = useState(
    Object.keys(skills).map((categoryName, idx) => ({
      id: idx + 1,
      name: categoryName,
      skills: skills[categoryName].skill, // Skills for this category
    }))
  );

  const handleAddCategory = () => {
    if (newCategory.trim()) {
      // Check if the category already exists
      if (!categories.some((cat) => cat.name === newCategory)) {
        const newCategoryObj = {
          id: categories.length + 1,
          name: newCategory,
          skills: [], // Start with an empty skills array
        };

        setCategories((prevCategories) => [...prevCategories, newCategoryObj]);
        setSelectedCategory(newCategory); // Automatically select the new category
        setNewCategory(''); // Clear the new category input
      }
    }
  };

  const handleAddLanguage = () => {
    if (languageInput && proficiencyInput) {
      const newLanguage = { id: languagesState.length + 1, name: languageInput, proficiency: proficiencyInput, userId: 1 };
      setLanguagesState((prevLanguages) => [...prevLanguages, newLanguage]);  
      const { id, ...newLanguageFormat } = newLanguage
      onCreateLanguage(newLanguageFormat); // Notify parent about the new language
      setLanguageInput('');
      setProficiencyInput('');
    }
  };

  const handleRemoveLanguage = (languageId?: number) => {
    onRemoveLanguage(languageId);
    setLanguagesState((prevLanguages) => prevLanguages.filter((language) => language.id !== languageId));
  };


  const handleAddSkill = () => {
    if (skillInput && selectedCategory) {
      const category = categories.find((cat) => cat.name === selectedCategory);
      const categoryId = category ? category.id : 0;

      onCreateSkill({ name: skillInput, categoryId, userId: 1 }); // Adjust userId as needed

      setCategories((prevCategories) =>
        prevCategories.map((cat) => 
          cat.name === selectedCategory
            ? {
                ...cat,
                skills: [...cat.skills, { name: skillInput }],
              }
            : cat
        )
      )
      setSkillInput(''); // Clear input after adding
    }
  };

  const handleRemoveSkill = (categoryName: string, skillId?: number) => {
    if (skillId === undefined) return;
    onRemoveSkill(skillId);
    setCategories((prevCategories) =>
      prevCategories.map((cat) => {
        if (cat.name === categoryName) {
          return {
            ...cat,
            skills: cat.skills.filter((skill) => skill?.id !== skillId),
          };
        }
        return cat;
      })
    );
  };

  const handleUpdate = () => {
    const updatedSkills = categories.flatMap((category) =>
      category.skills.map((skill) => ({
        id: skill?.id,
        name: skill.name,
        categoryId: category.id,
      }))
    );
    onUpdateSkill(updatedSkills);
  };

  console.log('categories', categories)
  return (
    <Container maxWidth="sm" sx={{ width: '86vw' }} >
      <Box sx={{
         mt: 1,
         display: 'flex',
         flexDirection: 'column',
         alignItems: 'flex-start'
       }}
      >
        {/* Category Selector */}
        <Box component="form" sx={{ mt: 1, width: '100%' }}>
          <FormControl fullWidth size='small'>
            <InputLabel id="category-select-label">Select Category</InputLabel>
            <Select 
              labelId="category-select-label"
              size='small'
              fullWidth
              label='Select Category'
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value as string)}
              sx={{ mb: 2 }}
        
            >
              {/* Placeholder option */}
              <MenuItem value="">
                <em>Select Category</em>
              </MenuItem>
              {categories.map((category) => (
                <MenuItem key={category.id} value={category.name}>
                  {category.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl> 
        </Box>
          
        {/* Add New Category */}
        <Box sx={{ display: 'flex', mb: 2 }}>
          <TextField
            size='small'
            label="New Category"
            variant="outlined"
            slotProps={{
              inputLabel: {
                shrink: true,
              },
            }}
            value={newCategory}
            onChange={(e) => setNewCategory(e.target.value)}
            fullWidth
          />
          <Button
            variant="contained"
            sx={{ ml: 2 }}
            onClick={handleAddCategory}
            disabled={!newCategory.trim()}
          >
            Add
          </Button>
        </Box>
          
        {/* Add Skill to Category */}
        {selectedCategory && (
          <Box sx={{ mb: 2 , width: '100%'}}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6">
                Skills in {selectedCategory}
              </Typography>
              <Box sx={{ display: 'flex', mt: 2 }}>
                <TextField
                  size='small'
                  label="New Skill"
                  variant="outlined"
                  slotProps={{
                    inputLabel: {
                      shrink: true,
                    },
                  }}
                  value={skillInput}
                  onChange={(e) => setSkillInput(e.target.value)}
                  fullWidth
                />
                <Button
                  variant="contained"
                  sx={{ ml: 2 }}
                  onClick={handleAddSkill}
                  disabled={!skillInput.trim()}
                >
                  Add
                </Button>
              </Box>
                
              {/* List of Skills */}
              <Box sx={{ mt: 2 }}>
                {categories
                  .find((cat) => cat.name === selectedCategory)
                  ?.skills.map((skill) => (
                    <Box
                      key={skill.id}
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 1,
                      }}
                    >
                      <Typography>{skill.name}</Typography>
                      <IconButton onClick={() => handleRemoveSkill(selectedCategory, skill.id)}>
                        <Remove />
                      </IconButton>
                    </Box>
                  ))}
              </Box>
            </Paper>
          </Box>
        )}
        {/* Languages Section */}
        <Box sx={{ mb: 2, width: '100%' }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6">Languages</Typography>
            <Box sx={{ display: 'flex', mt: 2 }}>
              <TextField
                size="small"
                label="Language"
                value={languageInput}
                onChange={(e) => setLanguageInput(e.target.value)}
                fullWidth
              />
              <TextField
                size="small"
                label="Proficiency"
                value={proficiencyInput}
                onChange={(e) => setProficiencyInput(e.target.value)}
                fullWidth
                sx={{ ml: 2 }}
              />
              <Button variant="contained" sx={{ ml: 2 }} onClick={handleAddLanguage} disabled={!languageInput.trim()}>
                Add
              </Button>
            </Box>

            {/* Displaying Added Languages */}
            <Box sx={{ mt: 2 }}>
              {languagesState.map((language) => (
                <Box key={language.id} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography>
                    {language.name} - {language.proficiency}
                  </Typography>
                  <IconButton onClick={() => handleRemoveLanguage(language.id)}>
                    <Remove />
                  </IconButton>
                </Box>
              ))}
            </Box>
          </Paper>
        </Box>
          <DialogActions>
            <Button  color="secondary" onClick={handleClose}>
              Cancel
            </Button>
            <Button color="primary" onClick={handleUpdate}>
              Update
            </Button>
          </DialogActions>
      </Box>
    </Container>
  );
};

export default SkillForm;
